/**
 * Project color helpers — derive a stable hue from a project's `code`
 * (or fallback seed) so the same project gets the same color dot across
 * every surface (AppShell project switcher, timesheet week grid,
 * deliverable search picker chips, future surfaces).
 *
 * The hue mapping was originally inlined in `TimesheetWeekGrid.tsx` and
 * `DeliverableSearchPicker.tsx`; centralizing here keeps every surface
 * literally aligned. The constants (31, 137.508) match the previous
 * inline implementations exactly so existing surfaces don't shift hue
 * on this consolidation.
 */

/**
 * Hash a string into a hue in [0, 360). Uses 31-prime accumulation +
 * golden-ratio multiplier for good visual spread across short codes
 * (3–6 character project codes like "MOTE", "AE", "PLAY").
 */
export function hashHue(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  return (Math.abs(hash) * 137.508) % 360
}

/**
 * Render-ready inline style for a project chip. The HUE is hashed from the
 * seed; the saturation + lightness come from the `--project-chip-*` CSS
 * vars declared per theme in `src/styles/global.css`, so the same chip is a
 * soft pastel tile on the light theme and a deep tinted tile on the two dark
 * ones. Baking 85%/96% in here produced a near-white tile on Obsidian /
 * Midnight — the same class of bug the `--chart-hashed-*` vars solve for the
 * reports palette, and the reason those vars exist.
 *
 * `borderColor`, NOT the `border` shorthand: an inline `border: <color>`
 * resets border-style to `none` (omitted shorthand components take their
 * initial value), which silently un-paints the edge the `border` utility on
 * the call site asks for. Width + style stay with Tailwind; only the hue
 * comes from here.
 *
 * Pair with monospace + uppercase to match the `<ProjectChip>` look used in
 * the deliverable picker.
 */
export function chipStyleFor(seed: string): {
  background: string
  color: string
  borderColor: string
} {
  const hue = hashHue(seed || 'default')
  return {
    background: `hsl(${hue} var(--project-chip-bg-s) var(--project-chip-bg-l))`,
    color: `hsl(${hue} var(--project-chip-fg-s) var(--project-chip-fg-l))`,
    borderColor: `hsl(${hue} var(--project-chip-border-s) var(--project-chip-border-l))`,
  }
}

/**
 * Solid color dot tone. Saturation + lightness chosen so the dot stays
 * legible on every surface it lands on across all three themes — the light
 * theme's white / slate popovers and the two dark themes' elevated panels
 * alike. A mid-lightness saturated hue is the one band that clears both, so
 * this helper stays theme-independent (unlike `chipStyleFor`, whose large
 * tinted fill has to invert).
 */
export function dotColorFor(seed: string): string {
  const hue = hashHue(seed || 'default')
  return `hsl(${hue} 65% 55%)`
}
